import * as React from "react";

/**
 * Globaler Z-Index Stack für überlagernde UI-Layer
 * (Dialog, AlertDialog, Sheet, custom Slide-Ins).
 *
 * Ziel: Ein neu geöffneter Layer liegt IMMER über dem aktuell
 * obersten Layer — unabhängig davon, aus welchem anderen Layer
 * er geöffnet wurde. Damit müssen einzelne Komponenten keinen
 * hardcoded z-index mehr wählen.
 *
 * Konvention:
 *  - BASE = 200 (über App-Chrome / Editor-Toolbars)
 *  - STEP = 20  (Platz für Overlay + Content)
 *  - Overlay = base
 *  - Content = base + 1
 *
 * Sonderfall AI-Generation-Overlay: nutzt weiterhin
 * z-[2147483000] und liegt damit bewusst über allem.
 */

const BASE = 200;
const STEP = 20;

const stack: number[] = [];

function peek(): number {
  return stack.length > 0 ? stack[stack.length - 1] : BASE - STEP;
}

function acquireLayer(): number {
  const next = peek() + STEP;
  stack.push(next);
  return next;
}

function releaseLayer(z: number): void {
  const idx = stack.lastIndexOf(z);
  if (idx >= 0) stack.splice(idx, 1);
}

/**
 * Hook: fordert beim Mount einen frischen Layer-Slot an und
 * gibt ihn beim Unmount frei. Rückgabe: { overlay, content }.
 *
 * Komponenten sollten das Ergebnis in `style={{ zIndex }}` setzen,
 * NICHT über Tailwind-Klassen — nur so gewinnt der dynamische
 * Wert gegen mögliche statische `z-*`-Klassen.
 */
export function useLayerZIndex(): { overlay: number; content: number } {
  const zRef = React.useRef<number | null>(null);
  if (zRef.current === null) {
    zRef.current = acquireLayer();
  }
  React.useEffect(() => {
    return () => {
      if (zRef.current !== null) {
        releaseLayer(zRef.current);
        zRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const base = zRef.current;
  return { overlay: base, content: base + 1 };
}

/**
 * Hook für Popper-Inhalte (Select, Popover, Dropdown-Menü).
 *
 * Anders als `useLayerZIndex` reserviert dieser Hook KEINEN Layer-Slot,
 * sondern legt sich beim Mount dynamisch über den aktuell obersten Layer.
 * Damit erscheinen Dropdowns immer über dem Dialog, aus dem sie geöffnet
 * wurden — unabhängig davon, wie hoch der Dialog-Stack schon gewachsen ist.
 */
export function usePopperZIndex(): number {
  const zRef = React.useRef<number | null>(null);
  if (zRef.current === null) {
    zRef.current = Math.max(peek() + STEP / 2, 300);
  }
  return zRef.current;
}
